/**
 * Portfolio table widget. One row per owned property with strategy + status
 * chips, monthly cash flow, monthly loan payment, and the next EMI date —
 * mirroring the design's `.ptable`.
 */
import { cn } from "@/lib/utils";

import { DashboardCard } from "./DashboardCard";
import {
  fmtMoney,
  statusLabel,
  STRAT_COLOR,
  type PortfolioRow,
  type StrategyKey,
} from "./dashboard-metrics";
import type { PropertyStatus } from "@/components/properties/property-metrics";

interface PortfolioTableCardProps {
  rows: PortfolioRow[];
}

const STRAT_CHIP: Record<StrategyKey, string> = {
  brrrr: "bg-blue-500/15 text-blue-400",
  flip: "bg-warning/15 text-warning",
  rental: "bg-success/15 text-success",
};

const STATUS_CHIP: Record<PropertyStatus, string> = {
  owned: "bg-success/15 text-success",
  "in_rehab": "bg-warning/15 text-warning",
  sold: "bg-muted text-muted-foreground",
};

export function PortfolioTableCard({ rows }: PortfolioTableCardProps) {
  return (
    <DashboardCard
      title="Portfolio"
      subtitle="Performance of every owned property"
      link={{ label: "View all properties", to: "/properties" }}
    >
      {rows.length === 0 ? (
        <div className="py-[18px] text-center text-[13px] text-muted-foreground">
          No owned properties yet. Move a deal through the pipeline to start
          tracking it here.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <Th>Property</Th>
                <Th>Strategy</Th>
                <Th>Status</Th>
                <Th num>Cash flow / mo</Th>
                <Th num>Monthly payment</Th>
                <Th num>EMI payment</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="group">
                  <Td>
                    <div className="font-semibold text-foreground">{r.addr1}</div>
                    {r.addr2 ? (
                      <div className="mt-0.5 text-[11.5px] text-muted-foreground">
                        {r.addr2}
                      </div>
                    ) : null}
                  </Td>
                  <Td>
                    <span
                      className={cn(
                        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-[3px] text-[11.5px] font-semibold",
                        STRAT_CHIP[r.strategyKey],
                      )}
                    >
                      <span
                        className="h-[7px] w-[7px] rounded-full"
                        style={{ background: STRAT_COLOR[r.strategyKey] }}
                      />
                      {r.strategyLabel}
                    </span>
                  </Td>
                  <Td>
                    <span
                      className={cn(
                        "inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-[3px] text-[11.5px] font-semibold",
                        STATUS_CHIP[r.status],
                      )}
                    >
                      {statusLabel(r.status)}
                    </span>
                  </Td>
                  <Td num>
                    <span
                      className={cn(
                        "font-semibold",
                        r.cashFlow >= 0 ? "text-success" : "text-destructive",
                      )}
                    >
                      {fmtMoney(r.cashFlow)}
                    </span>
                  </Td>
                  <Td num>{r.monthly}</Td>
                  <Td num>{r.emi}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardCard>
  );
}

function Th({ children, num }: { children: React.ReactNode; num?: boolean }) {
  return (
    <th
      className={cn(
        "whitespace-nowrap px-3.5 pb-3 text-[10.5px] font-bold uppercase tracking-[0.05em] text-muted-foreground",
        num ? "text-right tabular-nums" : "text-left",
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, num }: { children: React.ReactNode; num?: boolean }) {
  return (
    <td
      className={cn(
        "border-t border-border px-3.5 py-[13px] text-[13px] text-foreground transition-colors group-hover:bg-muted",
        num && "text-right tabular-nums",
      )}
    >
      {children}
    </td>
  );
}
